import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import {
  Card,
  Row,
  Col,
  Typography,
  Tabs,
  Button,
  Space,
  Input,
  Select,
  Table,
  Tag,
  Avatar,
  Modal,
  Form,
  message,
  Statistic,
  Tooltip,
  Divider,
  Badge,
  Dropdown,
  Popconfirm,
  Upload,
  Alert
} from 'antd';
import {
  UserOutlined,
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  PhoneOutlined,
  MailOutlined,
  ShoppingCartOutlined,
  CalendarOutlined,
  TeamOutlined,
  MoreOutlined,
  FilterOutlined,
  ExportOutlined,
  ImportOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import CustomerProfile from '../components/CustomerProfile';
import apiService from '../../../services/apiService';
import { useAuthStore } from '../../../stores/authStore';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  location: string;
  status: 'active' | 'inactive' | 'prospect' | 'lead';
  totalOrders: number;
  lastActivity: string;
  lastPurchase: string;
  createdAt: string;
  /**
   * The customer as the API returned it. The fields above are a display
   * shape and drop most of the record — contacts, address lines, payment
   * terms — which is why the first version of Export could not include them.
   */
  raw?: any;
}

const CustomerManagementPage: React.FC = () => {
  const { user } = useAuthStore();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerModalVisible, setCustomerModalVisible] = useState(false);
  const [profileModalVisible, setProfileModalVisible] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      // Real API call to fetch customers using apiService (includes auth token)
      // Use a high limit to get all customers, not hardcoded to 100
      const data: any = await apiService.get('/customers?page=1&limit=10000');
      
      // Transform API data to match our interface
      const transformedCustomers: Customer[] = data.customers.map((customer: any) => ({
        id: customer.id,
        name: customer.name,
        email: customer.email || '',
        phone: customer.phone || '',
        company: customer.company?.name || '',
        location: customer.address || '',
        status: customer.isActive ? 'active' : 'inactive',
        totalOrders: customer._count?.salesOrders || 0,
        lastActivity: new Date(customer.updatedAt).toISOString().split('T')[0],
        lastPurchase: customer._count?.salesOrders > 0 ? new Date(customer.updatedAt).toISOString().split('T')[0] : '',
        createdAt: new Date(customer.createdAt).toISOString().split('T')[0],
        raw: customer
      }));

      setCustomers(transformedCustomers);
      message.success(`Loaded ${transformedCustomers.length} customers from database`);
    } catch (error) {
      console.error('Error fetching customers:', error);
      // No sample fallback. This used to drop in five fictional customers
      // (John Smith at Acme Corporation, and so on) whenever the request
      // failed, which looked exactly like a successful load of somebody
      // else's data.
      message.error('The customer list could not be loaded. Please try again.');
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors = {
      active: 'green',
      inactive: 'red',
      prospect: 'blue',
      lead: 'orange'
    };
    return colors[status as keyof typeof colors] || 'default';
  };

  /**
   * Export what is on screen, not the whole table — the search box and status
   * filter are how they narrow it down, so exporting past that would ignore
   * what they just asked for.
   */
  const handleExport = () => {
    if (filteredCustomers.length === 0) {
      message.warning('Nothing to export with the current filters');
      return;
    }
    // Headers match the import template so an exported sheet can be edited and
    // imported back. The client reported the first version as missing "呢D
    // info" — the contacts and address block — because it only carried the
    // columns shown in the table.
    const rows = filteredCustomers.map(c => {
      const r = c.raw || {};
      return {
        'Name': c.name,
        'Email': c.email || '',
        'Phone': c.phone || '',
        'Address Line 1': r.addressLine1 || '',
        'Address Line 2': r.addressLine2 || '',
        'City': r.city || '',
        'State / Province': r.state || '',
        'Postal Code': r.postalCode || '',
        'Country': r.country || '',
        'Payment Terms': r.defaultPaymentTerms || '',
        'Primary Contact Name': r.primaryContactName || '',
        'Primary Contact Email': r.primaryContactEmail || '',
        'Primary Contact Phone': r.primaryContactPhone || '',
        'Billing Contact Name': r.billingContactName || '',
        'Billing Contact Email': r.billingContactEmail || '',
        'Billing Contact Phone': r.billingContactPhone || '',
        'Address': r.address || '',
        'Company': c.company || '',
        'Status': c.status || '',
        'Orders': c.totalOrders ?? 0,
        'Last Activity': c.lastActivity || '',
        'Created': c.createdAt || ''
      };
    });
    const sheet = XLSX.utils.json_to_sheet(rows);
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Customers');
    XLSX.writeFile(book, `Customers-${new Date().toISOString().slice(0, 10)}.xlsx`);
    message.success(`Exported ${rows.length} customer${rows.length === 1 ? '' : 's'}`);
  };

  /**
   * The template doubles as the column spec — the header row is what the
   * importer reads, so handing them the file is better than documenting it.
   */
  const IMPORT_COLUMNS = [
    'Name', 'Email', 'Phone', 'Address Line 1', 'Address Line 2', 'City', 'State / Province',
    'Postal Code', 'Country', 'Payment Terms',
    'Primary Contact Name', 'Primary Contact Email', 'Primary Contact Phone',
    'Billing Contact Name', 'Billing Contact Email', 'Billing Contact Phone'
  ];

  const COLUMN_TO_FIELD: Record<string, string> = {
    'Name': 'name', 'Customer': 'name', 'Customer Name': 'name',
    'Email': 'email', 'Phone': 'phone',
    'Address Line 1': 'addressLine1', 'Address Line 2': 'addressLine2',
    'City': 'city', 'State': 'state', 'State / Province': 'state',
    'Postal Code': 'postalCode', 'Country': 'country',
    'Payment Terms': 'defaultPaymentTerms',
    'Primary Contact Name': 'primaryContactName',
    'Primary Contact Email': 'primaryContactEmail',
    'Primary Contact Phone': 'primaryContactPhone',
    'Billing Contact Name': 'billingContactName',
    'Billing Contact Email': 'billingContactEmail',
    'Billing Contact Phone': 'billingContactPhone'
  };

  const handleDownloadTemplate = () => {
    const sheet = XLSX.utils.aoa_to_sheet([IMPORT_COLUMNS, ['Example Ltd', 'hello@example.com']]);
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Customers');
    XLSX.writeFile(book, 'Customer-Import-Template.xlsx');
  };

  /**
   * Parses the sheet and sends the rows. Nothing is created until the server
   * has checked every row, and it reports each one back — see
   * CustomerImportService.
   */
  const handleImportFile = async (file: File) => {
    setImporting(true);
    setImportResult(null);
    try {
      const data = await file.arrayBuffer();
      const book = XLSX.read(data);
      const sheet = book.Sheets[book.SheetNames[0]];
      const raw: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });

      if (raw.length === 0) {
        message.error('That file has no rows');
        return;
      }

      const rows = raw.map(r => {
        const row: Record<string, any> = {};
        Object.keys(r).forEach(header => {
          const field = COLUMN_TO_FIELD[String(header).trim()];
          if (field) row[field] = r[header];
        });
        return row;
      });

      const res: any = await apiService.post('/customers/import', { rows });
      const result = res?.data ?? res;
      setImportResult(result);

      if (result.created > 0) {
        message.success(`Imported ${result.created} customer${result.created === 1 ? '' : 's'}`);
        fetchCustomers();
      }
      if (result.failed?.length) {
        message.warning(`${result.failed.length} row(s) were skipped — see the details below`);
      }
    } catch (error: any) {
      message.error(`Import failed: ${error?.message || 'unknown error'}`);
    } finally {
      setImporting(false);
    }
  };

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer.company.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || customer.status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  const handleAddCustomer = () => {
    form.resetFields();
    setSelectedCustomer(null);
    setCustomerModalVisible(true);
  };

  const handleEditCustomer = async (customer: Customer) => {
    setSelectedCustomer(customer);

    // Fetch full customer data from API to get all address fields
    try {
      const fullCustomer: any = await apiService.get(`/customers/${customer.id}`);

      // Map API fields to form fields
      form.setFieldsValue({
        name: fullCustomer.name,
        email: fullCustomer.email,
        phone: fullCustomer.phone,
        company: fullCustomer.company?.name || '',
        location: fullCustomer.address,
        addressLine1: fullCustomer.addressLine1,
        addressLine2: fullCustomer.addressLine2,
        city: fullCustomer.city,
        state: fullCustomer.state,
        postalCode: fullCustomer.postalCode,
        country: fullCustomer.country,
        primaryContactName: fullCustomer.primaryContactName,
        primaryContactEmail: fullCustomer.primaryContactEmail,
        primaryContactPhone: fullCustomer.primaryContactPhone,
        billingContactName: fullCustomer.billingContactName,
        billingContactEmail: fullCustomer.billingContactEmail,
        billingContactPhone: fullCustomer.billingContactPhone,
        status: fullCustomer.isActive ? 'active' : 'inactive'
      });
    } catch (error) {
      console.error('Error fetching customer details:', error);
      // Fallback to using the customer object directly
      form.setFieldsValue(customer);
    }

    setCustomerModalVisible(true);
  };

  const handleViewCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setProfileModalVisible(true);
  };

  const handleDeleteCustomer = async (customerId: string) => {
    try {
      await apiService.deleteCustomerV2(customerId);
      message.success('Customer deleted successfully');
      // Refresh customer list
      await fetchCustomers();
    } catch (error: any) {
      console.error('Error deleting customer:', error);
      message.error(error.message || 'Failed to delete customer');
    }
  };

  const handleSaveCustomer = async (values: any) => {
    if (!user?.companyId) {
      message.error('User company not found. Please log in again.');
      return;
    }

    setSaving(true);
    try {
      if (selectedCustomer) {
        // Update existing customer via API
        const updateData = {
          name: values.name,
          email: values.email || undefined,  // Convert empty string to undefined
          phone: values.phone || undefined,
          address: values.location || undefined,
          addressLine1: values.addressLine1 || undefined,
          addressLine2: values.addressLine2 || undefined,
          city: values.city || undefined,
          state: values.state || undefined,
          postalCode: values.postalCode || undefined,
          country: values.country || undefined,
          primaryContactName: values.primaryContactName ?? undefined,
          primaryContactEmail: values.primaryContactEmail ?? undefined,
          primaryContactPhone: values.primaryContactPhone ?? undefined,
          billingContactName: values.billingContactName ?? undefined,
          billingContactEmail: values.billingContactEmail ?? undefined,
          billingContactPhone: values.billingContactPhone ?? undefined,
          isActive: values.status === 'active'
        };
        console.log('Updating customer with data:', JSON.stringify(updateData, null, 2));
        await apiService.updateCustomerV2(selectedCustomer.id, updateData);
        message.success('Customer updated successfully');
      } else {
        // Create new customer via API
        const createData = {
          name: values.name,
          email: values.email || undefined,  // Convert empty string to undefined
          phone: values.phone || undefined,
          address: values.location || undefined,
          addressLine1: values.addressLine1 || undefined,
          addressLine2: values.addressLine2 || undefined,
          city: values.city || undefined,
          state: values.state || undefined,
          postalCode: values.postalCode || undefined,
          country: values.country || undefined,
          primaryContactName: values.primaryContactName ?? undefined,
          primaryContactEmail: values.primaryContactEmail ?? undefined,
          primaryContactPhone: values.primaryContactPhone ?? undefined,
          billingContactName: values.billingContactName ?? undefined,
          billingContactEmail: values.billingContactEmail ?? undefined,
          billingContactPhone: values.billingContactPhone ?? undefined,
          companyId: user.companyId
        };
        console.log('Creating customer with data:', JSON.stringify(createData, null, 2));
        await apiService.createCustomerV2(createData);
        message.success('Customer added successfully');
      }
      setCustomerModalVisible(false);
      // Refresh the customer list from database
      await fetchCustomers();
    } catch (error: any) {
      console.error('Error saving customer:', error);
      // Check for validation error details from backend
      const details = error.response?.data?.details;
      if (details && Array.isArray(details)) {
        // Format validation errors for display
        const errorMessages = details.map((d: any) => `${d.path?.join('.') || 'field'}: ${d.message}`).join('; ');
        message.error(`Validation failed: ${errorMessages}`);
      } else {
        message.error(error.message || 'Failed to save customer');
      }
    } finally {
      setSaving(false);
    }
  };

  const customerColumns: ColumnsType<Customer> = [
    {
      title: 'Customer',
      key: 'customer',
      render: (_, record) => (
        <Space>
          <Avatar icon={<UserOutlined />} />
          <div>
            <div style={{ fontWeight: 'bold' }}>{record.name}</div>
            <div style={{ color: '#666', fontSize: '12px' }}>{record.company}</div>
          </div>
        </Space>
      )
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div>
          <div><MailOutlined /> {record.email}</div>
          <div><PhoneOutlined /> {record.phone}</div>
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Orders',
      dataIndex: 'totalOrders',
      key: 'totalOrders',
      sorter: (a, b) => a.totalOrders - b.totalOrders
    },
    {
      title: 'Last Activity',
      dataIndex: 'lastActivity',
      key: 'lastActivity',
      render: (date) => new Date(date).toLocaleDateString()
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Profile">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              onClick={() => handleViewCustomer(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              onClick={() => handleEditCustomer(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this customer?"
            onConfirm={() => handleDeleteCustomer(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button 
                type="text" 
                icon={<DeleteOutlined />} 
                danger
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Counted from what the records actually say. Total Revenue and Avg
  // Satisfaction used to live here, both totalled from Math.random() values, so
  // they changed every time the page was opened.
  const stats = {
    totalCustomers: customers.length,
    activeCustomers: customers.filter(c => c.status === 'active').length,
    totalOrders: customers.reduce((sum, c) => sum + (c.totalOrders || 0), 0),
    customersWithOrders: customers.filter(c => (c.totalOrders || 0) > 0).length
  };

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2}>
            <UserOutlined style={{ marginRight: 8 }} />
            Customer Management
          </Title>
        </Col>
        <Col>
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddCustomer}>
              Add Customer
            </Button>
            <Button icon={<ImportOutlined />} onClick={() => { setImportResult(null); setImportModalVisible(true); }}>
              Import
            </Button>
            <Button icon={<ExportOutlined />} onClick={handleExport}>
              Export
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Customers"
              value={stats.totalCustomers}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Active Customers"
              value={stats.activeCustomers}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Sales Orders"
              value={stats.totalOrders}
              prefix={<ShoppingCartOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="With Orders"
              value={stats.customersWithOrders}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Search
              placeholder="Search customers by name, email, or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '100%', maxWidth: 400 }}
            />
          </Col>
          <Col>
            <Select
              value={filterStatus}
              onChange={setFilterStatus}
              style={{ width: 120 }}
            >
              <Option value="all">All Status</Option>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
              <Option value="prospect">Prospect</Option>
              <Option value="lead">Lead</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Customer Table */}
      <Card>
        <Table
          columns={customerColumns}
          dataSource={filteredCustomers}
          rowKey="id"
          loading={loading}
          pagination={{
            //pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: [10, 20, 50, 100, 500],
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} customers`
          }}
        />
      </Card>

      {/* Add/Edit Customer Modal */}
      <Modal
        title={selectedCustomer ? 'Edit Customer' : 'Add New Customer'}
        open={customerModalVisible}
        onCancel={() => setCustomerModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveCustomer}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Customer Name"
                name="name"
                rules={[{ required: true, message: 'Please enter customer name' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Company"
                name="company"
                rules={[{ required: true, message: 'Please enter company name' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: 'Please enter email' },
                  { type: 'email', message: 'Please enter valid email' }
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Phone"
                name="phone"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Location"
                name="location"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          {/* Named people at the customer. The billing contact's email is what
              the invoice send dialog addresses invoices to. */}
          <Divider orientation="left" plain>Primary contact</Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Name" name="primaryContactName">
                <Input placeholder="Who to speak to" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Email"
                name="primaryContactEmail"
                rules={[{ type: 'email', message: 'Please enter a valid email' }]}
              >
                <Input placeholder="name@company.com" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Phone" name="primaryContactPhone">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>Billing contact</Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Name" name="billingContactName">
                <Input placeholder="Who handles invoices" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Email"
                name="billingContactEmail"
                rules={[{ type: 'email', message: 'Please enter a valid email' }]}
                extra="Invoices are sent here by default"
              >
                <Input placeholder="accounts@company.com" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Phone" name="billingContactPhone">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Address Line 1"
                name="addressLine1"
              >
                <Input placeholder="Street address" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Address Line 2"
                name="addressLine2"
              >
                <Input placeholder="Apartment, suite, etc. (optional)" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="City"
                name="city"
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="State / Province"
                name="state"
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Postal Code"
                name="postalCode"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label="Country"
            name="country"
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Status"
            name="status"
            rules={[{ required: true, message: 'Please select status' }]}
          >
            <Select>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
              <Option value="prospect">Prospect</Option>
              <Option value="lead">Lead</Option>
            </Select>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setCustomerModalVisible(false)} disabled={saving}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={saving}>
                {selectedCustomer ? 'Update' : 'Add'} Customer
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Customer Profile Modal */}
      <Modal
        title="Customer Profile"
        open={profileModalVisible}
        onCancel={() => setProfileModalVisible(false)}
        footer={null}
        // 800 squeezed the two-column dashboard into a few characters per line.
        width="min(1100px, 95vw)"
        style={{ top: 24 }}
      >
        {selectedCustomer && (
          <CustomerProfile 
            customerId={selectedCustomer.id}
            onClose={() => setProfileModalVisible(false)}
          />
        )}
      </Modal>

      {/* Import customers */}
      <Modal
        title="Import Customers"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        footer={<Button onClick={() => setImportModalVisible(false)}>Close</Button>}
        width={720}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Only new customers are created"
          description="A row is skipped if the name or email already exists, or appears twice in the file. Nothing already in the system is changed."
        />

        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Button onClick={handleDownloadTemplate}>Download template (.xlsx)</Button>

          <Upload.Dragger
            accept=".xlsx,.xls,.csv"
            maxCount={1}
            showUploadList={false}
            disabled={importing}
            beforeUpload={(file) => { handleImportFile(file as any); return false; }}
          >
            <p style={{ margin: 0, padding: 16 }}>
              {importing ? 'Importing…' : 'Click or drag an .xlsx / .csv file here'}
            </p>
          </Upload.Dragger>

          {importResult && (
            <div>
              <Alert
                type={importResult.failed?.length ? 'warning' : 'success'}
                showIcon
                message={`${importResult.created} customer(s) imported, ${importResult.failed?.length || 0} skipped`}
              />
              {importResult.failed?.length > 0 && (
                <Table
                  size="small"
                  style={{ marginTop: 12 }}
                  rowKey={(r: any) => `${r.row}`}
                  dataSource={importResult.failed}
                  pagination={{ pageSize: 8 }}
                  columns={[
                    { title: 'Row', dataIndex: 'row', width: 70 },
                    { title: 'Name', dataIndex: 'name' },
                    { title: 'Why it was skipped', dataIndex: 'reason' }
                  ]}
                />
              )}
            </div>
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default CustomerManagementPage; 